import { PropsWithChildren, useEffect, Suspense } from "react"
import {
  cn,
  NavigationMenuWrapper,
  ManagementMenu,
  ProfileDropdown,
  Skeleton,
  useCorrespondenceUnreadCountQuery,
} from "toptaal-shared"
import { useLogout } from "@/hooks/mutations/useAuth"
import { useOrganizationUserQuery } from "@/api/queries/organizationUser/useOrganizationUserQuery"
import { managementItems, navigationItems } from "@/constants/navigation"
import { useSetOrganizationColors } from "@/hooks/useSetOrganizationColors"
import { useOrganizationStyleQuery } from "@/api/queries/useOrganizationStyleQuery"
import { UserIcon } from "@heroicons/react/24/outline"

interface ScreenWrapperProps extends PropsWithChildren {
  showManagementMenu?: boolean
  className?: string
}

function NavigationContent() {
  const mutation = useLogout()
  const { data: user } = useOrganizationUserQuery()

  return (
    <ProfileDropdown
      firstName={user.firstName}
      lastName={user.lastName}
      variant="organization"
      menuItems={[
        {
          label: "Profiel",
          path: "/profiel/gegevens",
          icon: UserIcon,
        },
        {
          label: "Organisatie profiel",
          path: "/organisatie-profiel/gegevens",
          icon: UserIcon,
        },
      ]}
      onLogout={() => mutation.mutate()}
    />
  )
}

function OrganizationLogo() {
  const { data: organizationStyle } = useOrganizationStyleQuery()

  if (!organizationStyle?.companyPicture) {
    return <Skeleton className="w-8 h-8 rounded-none" />
  }

  return (
    <img
      src={organizationStyle.companyPicture}
      alt="Logo"
      className="w-8 h-8"
    />
  )
}

export default function ScreenWrapper({
  children,
  showManagementMenu = false,
  className,
}: ScreenWrapperProps) {
  const { data: organizationStyle, isSuccess } = useOrganizationStyleQuery()
  const setOrganizationColors = useSetOrganizationColors()
  const { data: unreadCount } = useCorrespondenceUnreadCountQuery()

  useEffect(() => {
    if (isSuccess && organizationStyle) {
      setOrganizationColors(organizationStyle)
    }
  }, [isSuccess, organizationStyle, setOrganizationColors])

  const navItems = navigationItems.map((item) => {
    if (item.path === "/correspondentie") {
      return { ...item, notificationCount: unreadCount }
    }

    return item
  })

  return (
    <>
      <NavigationMenuWrapper
        navigationItems={navItems}
        profileDropdown={
          <Suspense fallback={<Skeleton className="h-8 w-32" />}>
            <NavigationContent />
          </Suspense>
        }
        logo={<OrganizationLogo />}
      />
      {showManagementMenu && (
        <ManagementMenu managementItems={managementItems} />
      )}
      <div className={cn("screen-container", className)}>{children}</div>
    </>
  )
}
